import { useState } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { PackageSearch, Loader2, Truck, User, ShoppingBag, Calendar, ExternalLink, Info } from "lucide-react";

const shipBadge = (stato) => {
  const s = (stato || "").toLowerCase();
  if (s.includes("consegn")) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  if (s.includes("transito") || s.includes("spedit")) return "bg-blue-500/15 text-blue-300 border-blue-500/40";
  return "bg-amber-500/15 text-amber-300 border-amber-500/40"; // In preparazione
};

export default function RicercaOrdini() {
  const [numero, setNumero] = useState("");
  const [loading, setLoading] = useState(false);
  const [ordine, setOrdine] = useState(null);
  const [errore, setErrore] = useState("");

  const cerca = async (e) => {
    e?.preventDefault();
    const q = numero.trim();
    if (!q) return;
    setLoading(true);
    setErrore("");
    setOrdine(null);
    try {
      const { data } = await api.get("/shopify/ordine", { params: { numero: q } });
      setOrdine(data);
    } catch (err) {
      setErrore(err?.response?.data?.detail || "Ordine non trovato o errore durante la ricerca.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center">
            <PackageSearch className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight">Ricerca Ordini e Spedizioni</h1>
            <p className="text-slate-500 text-sm">Cerca un ordine Shopify e controlla lo stato della spedizione</p>
          </div>
        </div>
      </div>

      <form onSubmit={cerca} className="glass rounded-xl p-4 mb-5 flex flex-col sm:flex-row gap-3">
        <input
          data-testid="order-search-input"
          value={numero}
          onChange={(e) => setNumero(e.target.value)}
          placeholder="Inserisci Numero Ordine (es. #1001)..."
          className="flex-1 bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 text-slate-100 text-sm focus:border-cyan-400/60 outline-none"
        />
        <button
          data-testid="order-search-btn"
          type="submit"
          disabled={loading}
          className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold rounded-lg px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackageSearch className="w-5 h-5" />} Cerca Ordine
        </button>
      </form>

      {errore && (
        <div data-testid="order-error" className="glass rounded-xl p-5 border border-amber-500/30 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-300 mt-0.5" />
          <div className="text-sm text-slate-300">{errore}</div>
        </div>
      )}

      {ordine && (
        <div data-testid="order-result" className="space-y-4">
          {/* Testata ordine */}
          <div className="glass rounded-xl p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-heading text-2xl font-bold text-slate-100">{ordine.numero || ordine.id}</div>
                <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-1"><Calendar className="w-4 h-4" /> {ordine.data || "—"}</div>
              </div>
              <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${ordine.evaso ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-slate-700/40 text-slate-300 border-slate-600"}`}>
                {ordine.stato_evasione || (ordine.evaso ? "Evaso" : "Non Evaso")}
              </span>
            </div>
          </div>

          {/* Cliente */}
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading text-sm uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2"><User className="w-4 h-4" /> Cliente</h3>
            <div className="text-slate-100 font-semibold">{[ordine.cliente?.nome, ordine.cliente?.cognome].filter(Boolean).join(" ") || "—"}</div>
            <div className="text-sm text-slate-400 mt-1">{ordine.cliente?.email || "—"}</div>
            {ordine.cliente?.telefono && <div className="text-sm text-slate-400">{ordine.cliente.telefono}</div>}
          </div>

          {/* Articoli */}
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading text-sm uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Articoli ordinati</h3>
            <div className="space-y-2">
              {(ordine.articoli || []).map((a, i) => (
                <div key={`${a.sku || a.titolo}-${i}`} className="bg-slate-900/40 border border-slate-700/60 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <div className="text-slate-100 text-sm font-medium">{a.titolo}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {[a.variante, a.taglia && `Taglia ${a.taglia}`, a.grafica && `Grafica ${a.grafica}`, a.sku].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <span className="font-mono-tech text-slate-200 text-sm">×{a.quantita}</span>
                </div>
              ))}
              {(!ordine.articoli || ordine.articoli.length === 0) && <div className="text-slate-500 text-sm">Nessun articolo.</div>}
            </div>
          </div>

          {/* Spedizione */}
          <div className="glass rounded-xl p-5">
            <h3 className="font-heading text-sm uppercase tracking-wider text-slate-400 mb-3 flex items-center gap-2"><Truck className="w-4 h-4" /> Stato Spedizione</h3>
            <div className="flex flex-wrap items-center gap-3">
              <span data-testid="ship-status-badge" className={`px-3 py-1.5 rounded-lg text-sm font-semibold border ${shipBadge(ordine.spedizione?.stato)}`}>
                {ordine.spedizione?.stato || "In preparazione"}
              </span>
              {ordine.spedizione?.corriere && (
                <span className="text-sm text-slate-300">Corriere: <strong className="text-slate-100">{ordine.spedizione.corriere}</strong></span>
              )}
              {ordine.spedizione?.tracking_number && (
                <span className="text-sm text-slate-300 font-mono-tech">{ordine.spedizione.tracking_number}</span>
              )}
              {ordine.spedizione?.tracking_url && (
                <a href={ordine.spedizione.tracking_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 text-sm font-medium">
                  Traccia <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
